// src/hooks/useDictation.js
// STATUS: active | UPDATED: 2026-09-03
// LOG:
//   2026-09-03  New. Browser recording for the coach mic.
//
// Records a short take in the browser and hands the audio to our own transcribe
// function. Deliberately a hook rather than a component: the coach composer
// owns the button and where the words land, this owns the microphone and the
// mess around it.
//
// WHAT MAKES THIS FIDDLY, SO IT IS WRITTEN DOWN RATHER THAN REDISCOVERED
//
// Safari and Chrome do not record the same container. Chrome and Android give
// webm/opus, Safari and iOS give mp4/aac, and asking for a mimeType the browser
// does not support throws rather than falling back. So the type is negotiated
// against isTypeSupported and whatever wins is sent to the server as the
// content type, because the server has to tell Deepgram what it is receiving.
//
// The stream tracks have to be stopped by hand. Dropping the MediaRecorder is
// not enough: on a phone the browser keeps the microphone open, the orange
// recording dot stays lit, and a salesperson reasonably concludes we are
// listening to them all day.

import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { micGuidance, micPermissionState } from '../lib/micGuidance'

// Ordered by preference. Opus is a third the size of aac at the same
// intelligibility, which matters on a phone on dealership wifi.
const TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']

// Long enough for any word track, short enough that a phone left recording in
// somebody's pocket cannot run up a bill. The composer shows the countdown from
// ten seconds out.
const MAX_SECONDS = 120

// Below this, a press was a tap rather than a hold. Wispr Flow works because
// you hold a key and talk, and the web equivalent is holding the button. But a
// tap has to do something sensible too: somebody on a phone who taps and lets
// go should not get a quarter second of silence. So a short press latches on
// and waits for a second tap, and a real hold stops when the finger lifts.
const LATCH_MS = 400

export function useDictation({ onText }) {
  const [state, setState] = useState('idle') // idle | starting | recording | working
  const [error, setError] = useState(null)
  // Set alongside an error when the way out is a settings page rather than
  // trying again. The composer shows the steps instead of a shrug.
  const [guidance, setGuidance] = useState(null)
  const [seconds, setSeconds] = useState(0)

  const recorderRef = useRef(null)
  // Guards re-entry into start(). React state cannot do this job: setState is
  // asynchronous, so two press events landing in the same tick both read
  // state === 'idle' and both open a recorder.
  //
  // Tyler, 2026-09-03: "every time I hold the mic down, it's duplicating what
  // I'm saying." Two recorders were pushing into one shared chunk buffer, so
  // the blob held his audio twice and Deepgram faithfully transcribed both.
  const openingRef = useRef(false)
  const streamRef = useRef(null)
  const tickRef = useRef(null)
  const cancelledRef = useRef(false)
  // A separate boolean rather than treating a zero timestamp as "not pressing".
  // Date.now() is never zero so the sentinel works, right up until somebody
  // tests it with a fake clock or swaps in a monotonic one.
  const pressingRef = useRef(false)
  const pressedAtRef = useRef(0)
  const latchedRef = useRef(false)
  // Set when somebody lets go before the microphone has actually opened.
  // getUserMedia is fast once permission is granted and it is not instant.
  const stopWhenReadyRef = useRef(false)

  const supported =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator?.mediaDevices?.getUserMedia

  // Kill the microphone. Called on stop, on cancel, on unmount and on every
  // error path, because every one of those leaves the light on otherwise.
  const release = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop()
      streamRef.current = null
    }
    recorderRef.current = null
  }, [])

  useEffect(() => release, [release])

  const start = useCallback(async () => {
    // The ref is checked and set in the same synchronous breath, before any
    // await, so a second press in the same tick cannot get past it. The state
    // check stays as a second opinion but it is not the one doing the work.
    if (!supported || openingRef.current || state !== 'idle') return
    openingRef.current = true
    setError(null)
    setGuidance(null)
    cancelledRef.current = false

    // Ask before trying. Once somebody has denied the microphone the browser
    // will never prompt again and getUserMedia rejects instantly, so without
    // this the button looks like it simply does not work. Safari does not
    // answer this query at all, which is why null falls through to trying.
    if ((await micPermissionState()) === 'denied') {
      openingRef.current = false
      setGuidance(micGuidance())
      setError('Your browser is blocking the microphone.')
      return
    }
    // Set before the await, so the button reacts on the tap rather than
    // whenever the browser gets round to answering.
    setState('starting')
    try {
      // getUserMedia does not always settle. A permission prompt nobody answers,
      // a mic another app is holding, or an automated browser all leave it
      // pending forever, and the hook used to sit in idle with the person
      // staring at a button that did nothing. Silence where there should be a
      // signal is the same failure as the coach losing a drill.
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({
          // A dealership floor is not a quiet room.
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('MicTimeout')), 10000)
        ),
      ])
      // They hit discard while the prompt was still up.
      if (cancelledRef.current) {
        for (const track of stream.getTracks()) track.stop()
        openingRef.current = false
        setState('idle')
        return
      }
      streamRef.current = stream

      const mimeType = TYPES.find((t) => MediaRecorder.isTypeSupported?.(t)) || ''
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      recorderRef.current = recorder

      // Local to this recorder rather than a ref shared across all of them.
      // The shared buffer is what turned a double start into doubled audio
      // rather than into two separate takes, which would at least have been
      // obvious.
      const chunks = []
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data)
      }

      recorder.onstop = async () => {
        // A recorder that is no longer the current one has been superseded.
        // Its audio is a duplicate of somebody else's, so it is dropped.
        if (recorderRef.current && recorderRef.current !== recorder) return
        const type = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunks, { type })
        release()

        openingRef.current = false
        if (cancelledRef.current) {
          setState('idle')
          setSeconds(0)
          return
        }
        // A tap rather than a hold. Nothing to send and nothing to apologise for.
        if (blob.size < 1200) {
          setState('idle')
          setSeconds(0)
          return
        }

        setState('working')
        try {
          const text = await transcribe(blob, type)
          if (text) onText?.(text)
          else setError('Did not catch that.')
        } catch (err) {
          setError(err.message || 'Could not transcribe that.')
        } finally {
          setState('idle')
          setSeconds(0)
        }
      }

      recorder.start()
      setState('recording')
      setSeconds(0)

      // They already let go. Stop now rather than recording until they notice.
      if (stopWhenReadyRef.current) {
        stopWhenReadyRef.current = false
        recorder.stop()
        return
      }
      tickRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1
          // Stop ourselves rather than letting it run. The onstop handler above
          // still sends what was captured, so hitting the cap transcribes the
          // two minutes rather than throwing them away.
          if (next >= MAX_SECONDS && recorderRef.current?.state === 'recording') {
            recorderRef.current.stop()
          }
          return next
        })
      }, 1000)
    } catch (err) {
      release()
      openingRef.current = false
      setState('idle')
      if (err?.name === 'NotAllowedError') {
        // The other way in: they denied it at the prompt just now, or Safari
        // never told us it was already denied.
        setGuidance(micGuidance())
        setError('Your browser is blocking the microphone.')
      } else {
        setError(
          err?.message === 'MicTimeout'
            ? 'Still waiting on the microphone. Look for a permission prompt in your browser, then try again.'
            : err?.name === 'NotFoundError'
              ? 'No microphone found on this device. Plug one in, or type instead.'
              : 'Could not start recording. Try again, or type instead.'
        )
      }
    }
  }, [supported, state, onText, release])

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }, [])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    else {
      release()
      setState('idle')
      setSeconds(0)
    }
  }, [release])

  // Hold to talk, tap to latch. The component just forwards pointer events.
  const pressStart = useCallback(() => {
    if (state === 'recording') {
      // A tap while latched means stop.
      latchedRef.current = false
      pressingRef.current = false
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
      return
    }
    if (state !== 'idle') return
    pressingRef.current = true
    pressedAtRef.current = Date.now()
    latchedRef.current = false
    stopWhenReadyRef.current = false
    start()
  }, [state, start])

  const pressEnd = useCallback(() => {
    if (latchedRef.current) return
    if (!pressingRef.current) return
    pressingRef.current = false
    const held = Date.now() - pressedAtRef.current
    if (held < LATCH_MS) {
      // Too quick to be a hold. Keep recording and wait for the second tap.
      latchedRef.current = true
      return
    }
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    else stopWhenReadyRef.current = true
  }, [])

  return {
    supported,
    state,
    pressStart,
    pressEnd,
    latched: latchedRef.current,
    error,
    guidance,
    seconds,
    remaining: Math.max(0, MAX_SECONDS - seconds),
    start,
    stop,
    cancel,
    clearError: () => { setError(null); setGuidance(null) },
  }
}

async function transcribe(blob, contentType) {
  const { data } = await supabase.auth.getSession()
  const token = data?.session?.access_token
  if (!token) throw new Error('Sign in again.')

  const res = await fetch('/.netlify/functions/transcribe', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': contentType.split(';')[0] },
    body: blob,
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.error || 'Could not transcribe that.')
  return (body.text || '').trim()
}
